'use client';

import { useState, useRef, useEffect } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { Icon, useToast } from '@/src/components/ui';
import { Input } from '@workspace/ui/components/input';
import { uploadImage } from '@/src/actions/storage';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { Button } from '@workspace/ui/components/button';
import 'cropperjs/dist/cropper.css';
import { editProfile } from '@/src/actions/profile';
import { useProfileUIStore } from '../store/profile-ui-store';
import { cn } from '@workspace/ui/lib/utils';
import Image from 'next/image';

const circularCropperStyles = `
  .avatar-cropper .cropper-view-box,
  .avatar-cropper .cropper-face { border-radius: 50%; }
  .avatar-cropper .cropper-view-box { box-shadow: 0 0 0 1px #39f; outline: 0; }
  .avatar-cropper .cropper-face { background-color: rgba(0,0,0,0.1); }
`;

type AvatarProps = {
  avatarUrl: string;
  isMyProfile: boolean;
};

export const ProfileAvatar = ({ avatarUrl, isMyProfile }: AvatarProps) => {
  const [avatar, setAvatar] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRemoveDlgOpen, setIsRemoveDlgOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropperRef = useRef<ReactCropperElement>(null);

  const previewMode = useProfileUIStore((s) => s.previewMode);
  const isMobileView = useProfileUIStore((s) => s.isMobileView);
  const toast = useToast();

  useEffect(() => {
    if (avatarUrl) setAvatar(avatarUrl);
  }, [avatarUrl]);

  useEffect(() => {
    return () => {
      if (tempImage) URL.revokeObjectURL(tempImage);
    };
  }, [tempImage]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files[0]) return;

    const file = files[0];
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5 MB');
      toast.error('File size exceeds 5 MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setTempImage(reader.result as string);
      setIsDialogOpen(true);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleClick = () => fileInputRef.current?.click();

  const getRoundedCanvas = (canvas: HTMLCanvasElement) => {
    const roundCanvas = document.createElement('canvas');
    roundCanvas.width = canvas.width;
    roundCanvas.height = canvas.height;
    const ctx = roundCanvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context error');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      canvas.width / 2,
      0,
      Math.PI * 2
    );
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
    return roundCanvas;
  };

  const getCroppedImage = async () => {
    const cropper = cropperRef.current?.cropper;
    try {
      const canvas = cropper?.getCroppedCanvas({
        width: 200,
        height: 200,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      if (!canvas) {
        setError('Failed to crop image');
        return;
      }

      const roundCanvas = getRoundedCanvas(canvas);
      const dataUrl = roundCanvas.toDataURL('image/*');

      setAvatar(dataUrl);
      setIsDialogOpen(false);
      setTempImage(null);

      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const { data, error: uploadError } = await uploadImage({
        blob,
        type: 'avatar',
      });
      if (uploadError) {
        setError(uploadError.message);
        toast.error('Something went wrong while uploading the image');
        return;
      }

      if (data?.url) {
        await editProfile({ avatarUrl: data.url });
        toast.success('Avatar updated');
      }
    } catch (err) {
      console.error('Cropping error:', err);
      setError('Failed to crop image');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setTempImage(null);
    setError(null);
  };

  const openRemoveDialog = () => setIsRemoveDlgOpen(true);
  const closeRemoveDialog = () => setIsRemoveDlgOpen(false);

  const handleRemoveAvatar = async () => {
    try {
      await editProfile({ avatarUrl: null as unknown as string });
      setAvatar(null);
      setIsRemoveDlgOpen(false);
      toast.success('Avatar removed');
    } catch (e) {
      console.error(e);
      toast.error('Failed to remove avatar');
    }
  };

  return (
    <>
      <style jsx global>
        {circularCropperStyles}
      </style>

      <div
        className={cn('flex items-center h-fit mb-10 px-3 md:px-0', {
          'lg:mb-8': !isMobileView,
        })}
      >
        <div
          className={cn('relative w-[72px] h-[72px] rounded-full group', {
            'md:w-[211px] md:h-[211px]': !isMobileView,
          })}
        >
          <svg
            className="absolute top-0 left-0 w-full h-full"
            viewBox="0 0 211 211"
          >
            <circle
              cx="105.5"
              cy="105.5"
              r="105"
              fill="none"
              stroke="#D1D5DB"
              strokeWidth="1"
              strokeDasharray="15 6"
            />
          </svg>

          {!previewMode && isMyProfile && (
            <Input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileChange}
              accept="image/*"
            />
          )}

          <div className="absolute inset-0 flex items-center justify-center">
            {avatar ? (
              <div className="w-full h-full rounded-full overflow-hidden relative group">
                <Image
                  src={avatar}
                  alt="User avatar"
                  className="w-full h-full object-cover"
                  width={211}
                  height={211}
                  priority
                />

                {!previewMode && isMyProfile && (
                  <div
                    className={cn(
                      'absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-full opacity-0 transition-opacity',
                      {
                        'group-hover:opacity-100 bg-black/40': !isMobileView,
                        'bg-transparent': isMobileView,
                      }
                    )}
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleClick}
                      className="text-sm font-light text-white/70 hover:text-white hover:bg-black/30 text-center"
                    >
                      <Icon name="change" className="size-5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={openRemoveDialog}
                      className="text-white/70 hover:underline hover:text-white hover:bg-black/30 text-center"
                    >
                      <Icon name="delete-tile" className="size-5" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              !previewMode &&
              isMyProfile && (
                <button
                  type="button"
                  onClick={handleClick}
                  className="flex md:flex-row flex-col items-center justify-center md:gap-2 w-[90%] h-[90%] rounded-full group-hover:bg-bg-gray transition-colors ease-in-out"
                >
                  <Icon
                    name="upload"
                    className="md:size-6 size-4 text-gray-text group-hover:text-black transition-colors ease-in-out hidden md:block"
                  />
                  <span className="text-gray-text text-center md:text-sm text-[10px] font-medium group-hover:text-black transition-colors ease-in-out">
                    Add your avatar
                  </span>
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Avatar Preview</DialogTitle>
          </DialogHeader>

          <div className="w-full h-[300px] mt-4 avatar-cropper">
            {tempImage && (
              <Cropper
                src={tempImage}
                style={{ height: '100%', width: '100%' }}
                aspectRatio={1}
                guides={false}
                viewMode={1}
                minCropBoxHeight={10}
                minCropBoxWidth={10}
                background={false}
                responsive
                autoCropArea={0.8}
                checkOrientation={false}
                cropBoxMovable
                cropBoxResizable
                dragMode="move"
                toggleDragModeOnDblclick={false}
                ref={cropperRef}
              />
            )}
          </div>

          {error && <div className="text-red-error text-sm mt-2">{error}</div>}

          <DialogFooter className="flex justify-between mt-4">
            <Button variant="outline" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button onClick={getCroppedImage}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRemoveDlgOpen} onOpenChange={setIsRemoveDlgOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove avatar?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-black/70">
            This will remove your current avatar. You can upload a new one
            anytime.
          </p>
          <DialogFooter className="flex justify-between md:mt-4 mt-1">
            <Button
              size="sm"
              variant="outline"
              className="h-fit md:p-1 p-1.5"
              onClick={closeRemoveDialog}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="h-fit md:p-1 p-1.5"
              onClick={handleRemoveAvatar}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
